<template>
  <div class="min-h-screen bg-white">
    <!-- Intro Screen - Global but only active on Home -->
    <ClientOnly v-if="isHome">
      <HomeWelcomeScreen v-if="showIntro" @finished="handleIntroFinished" />
    </ClientOnly>

    <!-- Navigation -->
    <LayoutNavigation v-show="!showIntro || !isHome" />

    <!-- Page Content -->
    <main
      class="opacity-100 transition-opacity duration-1000 ease-in"
      :style="{ display: showIntro && isHome ? 'none' : 'block' }"
    >
      <slot />
    </main>

    <!-- Footer -->
    <LayoutFooter v-show="!showIntro || !isHome" />

    <!-- Cookie Consent Banner -->
    <ClientOnly>
      <UiCookieBanner />
      <UiPWAInstallPrompt />
    </ClientOnly>

    <!-- Global snackbar -->
    <Snackbar />

    <!-- Global Auth Modal - Lazy loaded only when needed -->
    <ClientOnly>
      <LazyAuthModal
        v-if="showAuthModal"
        :open="showAuthModal"
        :default-tab="authModalDefaultTab"
        @close="closeAuthModal"
      />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { defineAsyncComponent } from "vue";
import { useAuthModal } from "~/composables/auth/useAuthModal";
import HomeWelcomeScreen from "~/components/home/HomeWelcomeScreen.vue";

const Snackbar = defineAsyncComponent(
  () => import("~/components/ui/feedback/Snackbar.vue"),
);

const { showAuthModal, authModalDefaultTab, closeAuthModal } = useAuthModal();

// Intro Logic
const route = useRoute();
const isHome = computed(() => route.path === "/");

const introSeen = useCookie("tayba_intro_seen", {
  maxAge: 60 * 60 * 24 * 365,
  default: () => false,
});

// Start with cookie value for SSR (avoids flash in browser)
const showIntro = ref(!introSeen.value);

onMounted(() => {
  // In PWA standalone mode, sessionStorage is cleared on every app close.
  // This lets us show the animation on each new app launch without showing
  // it on every in-app navigation (sessionStorage persists during the session).
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    !!(window.navigator as any).standalone;

  if (isStandalone && !sessionStorage.getItem("tayba_intro_seen_session")) {
    showIntro.value = true;
  }
});

const handleIntroFinished = () => {
  showIntro.value = false;
  introSeen.value = true; // Persist for browser mode
  if (import.meta.client) {
    sessionStorage.setItem("tayba_intro_seen_session", "true"); // Persist for this PWA session
  }
};
</script>

<style scoped>
/* Page transitions removed - no animation */
</style>
